import { observable, computed, action } from 'mobx';
import { ProductAddedUseCase } from '../use-cases/ProductAddedUseCase';
import { AddToCartErrors } from '../constants/AddToCartErrors';
import { ProductSelectedUseCase } from '../use-cases/ProductSelectedUseCase';
import { SkuAddedToCollectionUseCase } from '../use-cases/SkuAddedToCollectionUseCase';
import { on, events } from 'elc-service-bus';
import { ProductBaseController } from './ProductBaseController';
import { ProductGridService } from '../domain/services/ProductGridService';
import { ProductRepository } from '../repositories/ProductRepository';
import { IProductCTAConfig } from '../../exported/product-cta/ProductCTA';

export class ProductCTAViewController extends ProductBaseController {
    @observable public addingToCart: boolean = false;
    // if the add to bag fails in the first instance, will persist the disabled state of the button
    @observable public disableAddToCart: boolean = false;
    @observable public isShowingSuccessLabel: boolean = false;
    @observable public productGridService: ProductGridService;
    @observable public isLoaded: boolean = false;
    private productRepository: ProductRepository;

    constructor(productGridService: ProductGridService, productRepository: ProductRepository) {
        super();

        this.productGridService = productGridService;
        this.productRepository = productRepository;
    }

    @action
    public initialize = (config: IProductCTAConfig) => {
        if (!config.skuId && config.selectedSku) {
            config.skuId = config.selectedSku.skuId;
        }
        this.product = this.productRepository.getProductBySkuId(config.skuId);

        this.setSelectedSkuId(config.skuId);
    };

    @action
    public addToBag = async (
        skuId: string,
        quantity?: number,
        showSuccessLabel?: boolean,
        replenishment?: number
    ) => {
        this.addingToCart = true;

        try {
            const addToBagUseCase = new ProductAddedUseCase(skuId, quantity, replenishment);
            await addToBagUseCase.execute();

            if (showSuccessLabel) {
                this.isShowingSuccessLabel = true;
            }
        } catch (error) {
            if (error.message === AddToCartErrors.ITEM_QTY_MAXIMUM_REACHED) {
                this.disableAddToCart = true;
            }
        }

        this.addingToCart = false;
    };

    public navigateToProduct = async (positionIndex: number) => {
        const { skuId } = this.selectedSku;
        const navigateToProductUseCase = new ProductSelectedUseCase(
            this.selectedSku.parentProduct.productUrl,
            skuId,
            positionIndex
        );
        await navigateToProductUseCase.execute();
    };

    public addSkuToCollection = async (skuId: string, quantity?: number) => {
        await new SkuAddedToCollectionUseCase(skuId, quantity).execute();
    };

    @computed
    get data() {
        return {
            isShowingSuccessLabel: this.isShowingSuccessLabel,
            disableAddToCart: this.disableAddToCart,
            addingToCart: this.addingToCart,
            selectedSku: this.selectedSku
        };
    }

    @on(events.CART_FAILURE)
    public cartFailure = () => {
        this.isShowingSuccessLabel = false;
    };

    @on(events.CART_UPDATED)
    public cartUpdated = () => {
        this.isShowingSuccessLabel = false;
    };
}
